import React, {useEffect, useRef, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Loading} from "../../utils/InlineComponents";
import {AppContext} from "../../HomePage";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {Dropdown} from "primereact/dropdown";
import {AutoComplete} from "primereact/autocomplete";
import {InputText} from "primereact/inputtext";
import {MultiSelect} from "primereact/multiselect";
import {SelectButton} from "primereact/selectbutton";
import {Tag} from "primereact/tag";
import {getKeyValue} from "../../utils/Utils";
import {CanaryService} from "../../service/CanaryService";
import {ProxyMonitorService} from "../../service/ProxyMonitorService";
import AdminPageHeader from "./AdminPageHeader";
import {serves, asGroups, flatCaps} from "../../utils/ProxyServing";
import {SiteRuleDialog} from "./SiteRuleDialog";
import CanarySiteBands, {siteState, rateColor, retryRatio} from "./CanarySiteBands";

const DAY_OPTIONS = [{label: '1d', value: 1}, {label: '2d', value: 2}, {label: '3d', value: 3},
                     {label: '7d', value: 7}, {label: '14d', value: 14}, {label: '30d', value: 30},
                     {label: '60d', value: 60}];
const FAIL_REASONS = ['ban', 'timeout', 'no_data', 'error'];
// Outcomes that are answers rather than failures: the fetch worked and the site said the
// product is gone, or an import refused to re-create a product the campaign already tracks.
// canary_metrics leaves them out of the success rate, so show them apart from the failures --
// how much of a catalogue has died is worth seeing, but it is not a mark against the provider.
const INFO_REASONS = ['gone', 'duplicate'];
// Worst first: the bands read top-down as a triage queue.
const STATE_ORDER = {critical: 0, warning: 1, ab: 2, healthy: 3, no_data: 4, paused: 5};

// canary_metrics buckets one doc per UTC day, so every window calculation here is UTC too —
// matching the backend's `days` filter (scraper/views_canary.py).
const utcDay = (daysAgo) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - daysAgo);
    return d.toISOString().slice(0, 10);
};
const dayAfter = (date) => {
    const d = new Date(date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
};

// Roll daily docs up per provider|host: sum counters, keep the latest sample per reason
// (docs arrive sorted by date desc, so the first sample seen for a reason is the newest).
const aggregate = (docs) => {
    const map = {};
    (docs || []).forEach(d => {
        const key = d.provider + '|' + d.host;
        let m = map[key];
        if (!m) m = map[key] = {provider: d.provider, host: d.host, attempts: 0, success: 0, fail: 0,
                                proxyAttempts: null, proxyRequests: null, by_reason: {}, samples: {}};
        m.attempts += d.attempts || 0;
        // The attempt count travels with the request count it is divided by -- both come from
        // proxy_metrics, over the same population (see views_canary.metrics). Stays null when
        // no day carried a count: a spider that fetches from an aux API host files both under
        // that host, so the storefront row has no counterpart and a 0 would read as "never
        // retried" instead of "not known".
        if (d.proxy_attempts != null) m.proxyAttempts = (m.proxyAttempts || 0) + d.proxy_attempts;
        if (d.proxy_requests != null) m.proxyRequests = (m.proxyRequests || 0) + d.proxy_requests;
        m.success += d.success || 0;
        m.fail += d.fail || 0;
        Object.entries(d.by_reason || {}).forEach(([r, n]) => { m.by_reason[r] = (m.by_reason[r] || 0) + n; });
        Object.entries(d.samples || {}).forEach(([r, s]) => { if (!m.samples[r]) m.samples[r] = s; });
    });
    return Object.values(map).map(m => ({
        ...m, id: m.provider + '|' + m.host,
        successRate: m.attempts ? Math.round((m.success / m.attempts) * 1000) / 10 : null
    }));
};

const Canary = () => {

    const {t, toast, setError} = React.useContext(AppContext);
    const canaryService = new CanaryService();
    const proxyService = new ProxyMonitorService();

    const [rules, setRules] = useState([]);
    const [providers, setProviders] = useState([]);
    const [hosts, setHosts] = useState([]);
    const [hostSuggestions, setHostSuggestions] = useState([]);
    const [poolProxies, setPoolProxies] = useState([]);
    const [siteRules, setSiteRules] = useState([]);  // [{host, requires:[...]}] — capability gating
    const [rawDocs, setRawDocs] = useState([]);              // raw canary_metrics daily docs
    const [dimensions, setDimensions] = useState({hosts: [], providers: [], users: []});
    const [days, setDays] = useState(1);                     // opens on the last day
    const [resultView, setResultView] = useState('ab');      // 'ab' (compare) | 'breakdown' | 'daily'
    const [groupBy, setGroupBy] = useState('host,provider'); // breakdown grouping
    const [fltHost, setFltHost] = useState(null);            // shared filter bar
    const [fltProvider, setFltProvider] = useState(null);
    const [fltUser, setFltUser] = useState(null);
    const [search, setSearch] = useState('');                // site search over the bands
    const [expandedHost, setExpandedHost] = useState(null);  // A/B card detail toggle
    const [expandedRows, setExpandedRows] = useState(null);  // breakdown samples expansion
    const [proxiesRule, setProxiesRule] = useState(null);  // rule whose proxies dialog is open
    const [proxyFilters, setProxyFilters] = useState({source: null, type: null, enabled: null});
    const [siteRuleTarget, setSiteRuleTarget] = useState(null);  // unblocker site-rule being edited from a card
    const proxyDt = useRef(null);

    const [loading, setLoading] = useState(false);
    const [metricsLoading, setMetricsLoading] = useState(false);

    const [rowData, setRowData] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const newRule = () => ({host: null, provider: 'InHouseUnblocker', rate: 0.001, enabled: true});

    useEffect(() => {
        loadRules();
        canaryService.getProviders().then(setProviders).catch(() => {});
        canaryService.getHosts().then(setHosts).catch(() => {});
        proxyService.getPool().then(d => setPoolProxies((d && d.proxies) || [])).catch(() => {});
        proxyService.getSiteRules().then(d => setSiteRules((d && d.rules) || [])).catch(() => {});
    }, []);

    // Proxies from the pool eligible to serve a given host — capability + reservation + deny
    // gates, exactly as the unblocker routes (shared with Proxy Management; see
    // utils/ProxyServing). This is why e.g. hepsiburada (requires source:iproyal +
    // type:datacenter) is served by fewer proxies than the pool total.
    const eligibleFor = (host) => poolProxies.filter(p => serves(p, host, siteRules));

    // ---- unblocker site-rule (capability routing) — view + edit per canary site ----
    const siteRuleFor = (host) => siteRules.find(r => r.host === host);
    // Capability suggestions for the editor palette: stored tags ∪ site_rules ∪ derived tokens + seeds.
    const capabilityOptions = [...new Set([
        ...poolProxies.flatMap(p => p.capabilities || []),
        ...siteRules.flatMap(r => flatCaps(r.requires)),
        ...poolProxies.flatMap(p => [p.type && ('type:' + p.type), p.source && ('source:' + p.source),
            p.country && ('geo:' + String(p.country).toLowerCase())].filter(Boolean)),
        'hb-ok', 'pazarama-ok'])].sort();
    const openSiteRule = (host) => {
        const r = siteRuleFor(host) || {};
        const groups = asGroups(r.requires);
        setSiteRuleTarget({host, groups: groups.length ? groups.map(g => [...g]) : [[]], activeGroup: 0,
            requires_render: !!r.requires_render, max_concurrency: r.max_concurrency || 0, existing: true});
    };
    const saveSiteRule = () => {
        const host = ((siteRuleTarget && siteRuleTarget.host) || '').trim();
        if (!host) return;
        const groups = (siteRuleTarget.groups || [])
            .map(g => (g || []).map(c => (c || '').trim()).filter(Boolean)).filter(g => g.length);
        const requires = groups.length === 0 ? [] : (groups.length === 1 ? groups[0] : groups);
        const maxConcurrency = Math.max(0, parseInt(siteRuleTarget.max_concurrency, 10) || 0);
        proxyService.setSiteRule(host, requires, !!siteRuleTarget.requires_render, maxConcurrency)
            .then(() => proxyService.getSiteRules()).then(d => setSiteRules((d && d.rules) || []))
            .then(() => setSiteRuleTarget(null))
            .catch(error => { setError(error); setError(null); });
    };

    // Column filters for the proxies dialog table (PrimeReact 6: dt.filter(value, field, 'in')).
    const onProxyFilterChange = (e) => {
        const {name, value} = e.target;
        setProxyFilters({...proxyFilters, [name]: value});
        proxyDt.current.filter(value, name, 'in');
    };

    // Only registered sites (price_xpath) can host a canary; suggest from that list.
    const searchHosts = (e) => {
        const q = (e.query || '').toLowerCase();
        const matches = q ? hosts.filter(h => h.host.toLowerCase().includes(q)) : hosts;
        setHostSuggestions(matches.map(h => h.host).slice(0, 200));
    };

    useEffect(() => {
        loadMetrics();
    }, [days]);

    const loadRules = () => {
        setLoading(true);
        canaryService.getRules().then(data => {
            setRules(data);
            setLoading(false);
        }).catch(error => { setError(error); setError(null); setLoading(false); });
    };

    const loadMetrics = () => {
        setMetricsLoading(true);
        // One day MORE than the window is fetched so the daily lens can compare the oldest
        // visible day against its predecessor — at the 1d default there would be nothing to
        // compare otherwise. That extra day is trimmed off everywhere else (see windowStart).
        canaryService.getMetrics({days: days + 1}).then(res => {
            // Tolerate both the new {rows, dimensions} shape and the legacy bare-array response,
            // so frontend/backend deploy order doesn't matter.
            setRawDocs((res && res.rows) || (Array.isArray(res) ? res : []));
            setDimensions((res && res.dimensions) || {hosts: [], providers: [], users: []});
            setMetricsLoading(false);
        }).catch(error => { setError(error); setError(null); setMetricsLoading(false); });
    };

    const onChange = (e) => {
        const {key, value} = getKeyValue(e);
        setRowData({...rowData, [key]: value});
    };

    const saveRule = () => {
        canaryService.saveRule(rowData).then(() => {
            setShowFormModal(false);
            setRowData(newRule());
            loadRules();
            loadMetrics();
            toast.current.show({severity: 'success', summary: t('actions.save'), detail: t('message_detail.successful')});
        }).catch(error => { setError(error); setError(null); setShowFormModal(false); });
    };

    const deleteRule = () => {
        canaryService.deleteRule(rowData.host).then(() => {
            setShowDeleteModal(false);
            setRowData(newRule());
            loadRules();
            toast.current.show({severity: 'success', summary: t('actions.remove'), detail: t('message_detail.successful')});
        }).catch(error => { setError(error); setError(null); setShowDeleteModal(false); });
    };

    const toggleEnabled = (rule) => {
        canaryService.saveRule({
            host: rule.host, provider: rule.canary.provider,
            rate: rule.canary.rate, enabled: !(rule.canary.enabled !== false)
        }).then(() => loadRules()).catch(error => { setError(error); setError(null); });
    };

    const eligibleTable = (host) => {
        const el = eligibleFor(host);
        if (!el.length) return <span>{t('canary.no_proxies')}</span>;
        const statusTag = (p) => p.enabled
            ? <Tag value={t('proxy_monitor.enabled')} severity="success"/>
            : <Tag value={p.health_disabled ? t('proxy_monitor.health_disabled') : t('proxy_monitor.disabled')} severity="danger"/>;
        const opts = (key) => [...new Set(el.map(p => p[key]).filter(v => v != null))].map(v => ({label: v, value: v}));
        const srcFilter = <MultiSelect name="source" value={proxyFilters.source} options={opts('source')} onChange={onProxyFilterChange} style={{width: '100%'}}/>;
        const typFilter = <MultiSelect name="type" value={proxyFilters.type} options={opts('type')} onChange={onProxyFilterChange} style={{width: '100%'}}/>;
        const stFilter = <MultiSelect name="enabled" value={proxyFilters.enabled} onChange={onProxyFilterChange} style={{width: '100%'}}
                                      options={[{label: t('proxy_monitor.enabled'), value: true}, {label: t('proxy_monitor.disabled'), value: false}]}/>;
        return <DataTable ref={proxyDt} value={el} dataKey="ip" paginator={el.length > 20} rows={20}>
            <Column field="ip" header={t('proxy_monitor.ip')} sortable={true}/>
            <Column field="source" header={t('proxy_monitor.source')} sortable={true} filter={true} filterElement={srcFilter}/>
            <Column field="type" header={t('proxy_monitor.type')} sortable={true} filter={true} filterElement={typFilter}/>
            <Column field="enabled" header={t('proxy_monitor.status')} body={statusTag} sortable={true} sortField="enabled" filter={true} filterElement={stFilter}/>
            <Column field="last_egress" header={t('proxy_monitor.last_egress')}/>
        </DataTable>;
    };

    // ---- shared filter bar: applies to ALL result lenses (A/B cards + breakdown + daily) ----
    // `windowStart` is the first day the user actually asked for; anything older is the extra
    // day fetched for the daily delta and must not leak into any total.
    const windowStart = utcDay(days - 1);
    const inWindow = (d) => d.date >= windowStart;
    const filteredAll = rawDocs.filter(d =>
        (!fltHost || d.host === fltHost) &&
        (!fltProvider || d.provider === fltProvider) &&
        (!fltUser || d.user === fltUser));
    const filteredDocs = filteredAll.filter(inWindow);
    const metrics = aggregate(filteredDocs);  // per provider|host, for the A/B comparison

    // ---- A/B results: one card per host, canary arm vs baseline arm ----
    // The canary provider for a host is whatever its rule targets (InHouseUnblocker);
    // every other provider serving that host is the baseline it is measured against.
    // Rules are matched by domain suffix (an n11.com rule covers urun.n11.com), preferring
    // the most specific rule when several apply.
    const canaryProviderFor = (host) => {
        const h = (host || '').toLowerCase();
        let best = null;
        rules.forEach(r => {
            const rh = (r.host || '').toLowerCase();
            if (r.canary && (h === rh || h.endsWith('.' + rh)) && (!best || rh.length > best.host.length)) {
                best = r;
            }
        });
        return best && best.canary ? best.canary.provider : null;
    };
    // Merge several provider rows into a single baseline arm (usually just one).
    const aggArms = (arms) => {
        const agg = {attempts: 0, success: 0, proxyAttempts: null, proxyRequests: null,
                     by_reason: {}, samples: {}};
        arms.forEach(a => {
            agg.attempts += a.attempts; agg.success += a.success;
            if (a.proxyAttempts != null) agg.proxyAttempts = (agg.proxyAttempts || 0) + a.proxyAttempts;
            if (a.proxyRequests != null) agg.proxyRequests = (agg.proxyRequests || 0) + a.proxyRequests;
            FAIL_REASONS.concat(INFO_REASONS).forEach(r => { if (a.by_reason[r]) agg.by_reason[r] = (agg.by_reason[r] || 0) + a.by_reason[r]; });
            Object.entries(a.samples).forEach(([r, s]) => { if (!agg.samples[r]) agg.samples[r] = s; });
        });
        agg.successRate = agg.attempts ? Math.round(agg.success / agg.attempts * 1000) / 10 : null;
        agg.provider = arms.length === 1 ? arms[0].provider : (arms.length + ' ' + t('canary.provider'));
        return agg;
    };
    const buildComparisons = (ms) => {
        const byHost = {};
        ms.forEach(m => { (byHost[m.host] = byHost[m.host] || []).push(m); });
        return Object.entries(byHost).map(([host, arms]) => {
            const cp = canaryProviderFor(host);
            // InHouseUnblocker is always the in-house arm under test, so treat it as the
            // canary even when no rule row covers this exact host (e.g. an uncovered subdomain).
            let canary = arms.find(a => a.provider === cp)
                || arms.find(a => a.provider === 'InHouseUnblocker') || null;
            const rest = arms.filter(a => a !== canary);
            const baseline = rest.length ? aggArms(rest) : null;
            const delta = (canary && baseline && canary.successRate != null && baseline.successRate != null)
                ? Math.round((canary.successRate - baseline.successRate) * 10) / 10 : null;
            return {host, canary, baseline, delta,
                    total: (canary ? canary.attempts : 0) + (baseline ? baseline.attempts : 0)};
        }).sort((a, b) => b.total - a.total);
    };
    const comparisons = buildComparisons(metrics);              // filtered -> Results A/B lens
    const cmpByHost = {};                                       // unfiltered -> per-site band summaries
    buildComparisons(aggregate(rawDocs.filter(inWindow))).forEach(c => { cmpByHost[c.host] = c; });
    // Per-host daily series, oldest first. The day-over-day delta on the site cards and the
    // Daily lens both read from this, so a card can never disagree with its row.
    const dailySeries = (docs) => {
        const byDate = {};
        (docs || []).forEach(d => { if (d.date) (byDate[d.date] = byDate[d.date] || []).push(d); });
        const perHost = {};
        Object.keys(byDate).sort().forEach(date => {
            buildComparisons(aggregate(byDate[date])).forEach(c => {
                (perHost[c.host] = perHost[c.host] || []).push({...c, date});
            });
        });
        return perHost;
    };
    // How the canary arm moved on the newest day in the window against the day before it. The
    // extra day fetched beyond the window (see loadMetrics) exists exactly for this comparison;
    // a gap in traffic breaks the chain and leaves the delta empty rather than comparing across it.
    const dodByHost = (() => {
        const out = {};
        Object.entries(dailySeries(rawDocs)).forEach(([host, series]) => {
            const inside = series.filter(inWindow);
            const cur = inside[inside.length - 1];
            if (!cur || !cur.canary || cur.canary.successRate == null) return;
            const prev = series[series.indexOf(cur) - 1];
            if (!prev || dayAfter(prev.date) !== cur.date || !prev.canary
                || prev.canary.successRate == null) return;
            out[host] = Math.round((cur.canary.successRate - prev.canary.successRate) * 10) / 10;
        });
        return out;
    })();
    // Traffic across every canary site in the window — the one-line answer above the bands.
    const overall = aggregate(rawDocs.filter(inWindow)).reduce(
        (a, m) => ({attempts: a.attempts + m.attempts, success: a.success + m.success}),
        {attempts: 0, success: 0});
    // One prepared item per rule. Everything the bands need is decided here; CanarySiteBands
    // only chooses how much of the screen each state is worth.
    const sites = rules
        .filter(r => !search.trim() || (r.host || '').toLowerCase().includes(search.trim().toLowerCase()))
        .map(rule => {
            const cmp = cmpByHost[rule.host];
            const canary = cmp && cmp.canary;
            const el = eligibleFor(rule.host);
            const active = el.filter(p => p.enabled).length;
            const traffic = rule.canary && rule.canary.rate != null
                ? Number((rule.canary.rate * 100).toFixed(3)) : null;
            return {
                host: rule.host, rule, cmp,
                state: siteState(rule, cmp),
                enabled: !!(rule.canary && rule.canary.enabled !== false),
                rate: canary ? canary.successRate : null,
                dod: dodByHost[rule.host] != null ? dodByHost[rule.host] : null,
                attempts: canary ? canary.attempts : 0,
                proxyAttempts: canary ? canary.proxyAttempts : null,
                proxyRequests: canary ? canary.proxyRequests : null,
                failures: canary ? FAIL_REASONS.filter(r => canary.by_reason[r])
                    .map(r => ({reason: r, count: canary.by_reason[r]})) : [],
                info: canary ? INFO_REASONS.filter(r => canary.by_reason[r])
                    .map(r => ({reason: r, count: canary.by_reason[r]})) : [],
                meta: [rule.canary && rule.canary.provider, rule.proxy_type, rule.proxy_country].filter(Boolean),
                // Only worth showing when it is not the full 100%: next to the success rate a
                // second percentage reads like a second health number.
                trafficRate: traffic != null && traffic < 100 ? traffic : null,
                // The pool total is a fact about the pool, not the site. It only earns a badge
                // when a proxy is down or a site rule narrows the pool for this host.
                proxies: {active, total: el.length,
                          show: active < el.length || !!siteRuleFor(rule.host),
                          severity: active === 0 ? 'danger' : (active < el.length ? 'warning' : 'info')}
            };
        })
        .sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || b.attempts - a.attempts);
    const failuresInline = (m) => FAIL_REASONS.concat(INFO_REASONS).filter(r => m.by_reason[r])
        .map(r => `${r} ${m.by_reason[r]}`).join(' · ');

    const armCard = (arm, isCanary) => arm ? (
        <div style={{flex: 1, minWidth: 0, borderRadius: '8px', padding: '10px 12px',
                     border: '1px solid ' + (isCanary ? 'var(--primary-color, #3b82f6)' : 'var(--surface-border)'),
                     background: isCanary ? 'rgba(59,130,246,0.06)' : 'transparent'}}>
            <div style={{fontSize: '12px', color: 'var(--text-color-secondary)', marginBottom: '4px'}}>
                {(isCanary ? t('canary.canary_arm') : t('canary.baseline')) + ' · '}
                <b style={{color: 'var(--text-color)'}}>{arm.provider}</b>
            </div>
            <div style={{fontSize: '22px', fontWeight: 600, lineHeight: 1, color: rateColor(arm.successRate)}}>
                {arm.successRate != null ? arm.successRate + '%' : '-'}
            </div>
            <div style={{height: '6px', borderRadius: '6px', background: 'var(--surface-border)', margin: '8px 0 6px', overflow: 'hidden'}}>
                <span style={{display: 'block', height: '100%', width: (arm.successRate || 0) + '%', background: rateColor(arm.successRate)}}/>
            </div>
            <div style={{fontSize: '12px', color: 'var(--text-color-secondary)'}}>
                {(arm.attempts || 0).toLocaleString()} {t('canary.attempts')}
                {retryRatio(arm.proxyRequests, arm.proxyAttempts) != null &&
                    <span title={t('canary.retry_ratio_hint',
                                   {attempts: (arm.proxyAttempts || 0).toLocaleString(),
                                    requests: (arm.proxyRequests || 0).toLocaleString()})}>
                        {' · ×' + retryRatio(arm.proxyRequests, arm.proxyAttempts).toFixed(2)}
                    </span>}
                {failuresInline(arm) ? ' · ' + failuresInline(arm) : ''}
            </div>
        </div>
    ) : (
        <div style={{flex: 1, minWidth: 0, border: '1px dashed var(--surface-border)', borderRadius: '8px',
                     padding: '10px 12px', color: 'var(--text-color-secondary)', fontSize: '13px',
                     display: 'flex', alignItems: 'center'}}>
            {t('canary.no_baseline')}
        </div>
    );

    const comparisonCard = (c) => (
        <div key={c.host} style={{background: 'var(--surface-card)', border: '1px solid var(--surface-border)',
                                  borderRadius: '12px', padding: '14px 16px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                <span style={{fontWeight: 600}}>{c.host}</span>
                {(c.canary || c.baseline) &&
                    <Button className="p-button-text p-button-sm" label={t('canary.detail')}
                            icon={expandedHost === c.host ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                            onClick={() => setExpandedHost(expandedHost === c.host ? null : c.host)}/>}
            </div>
            <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
                {armCard(c.canary, true)}
                {armCard(c.baseline, false)}
            </div>
            {c.delta != null &&
                <div style={{marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <Tag value={'Δ ' + (c.delta >= 0 ? '+' : '') + c.delta + ' ' + t('canary.points')}
                         severity={c.delta > 0 ? 'success' : (c.delta < 0 ? 'danger' : 'info')}/>
                    <span style={{color: 'var(--text-color-secondary)', fontSize: '13px'}}>
                        {c.delta > 0 ? t('canary.ahead') : (c.delta < 0 ? t('canary.behind') : t('canary.even'))}
                    </span>
                </div>}
            {expandedHost === c.host &&
                <div style={{marginTop: '12px', paddingTop: '10px', borderTop: '1px solid var(--surface-border)'}}>
                    <div style={{fontSize: '12px', fontWeight: 600, marginBottom: '4px'}}>
                        {t('canary.canary_arm')}{c.canary ? ' · ' + c.canary.provider : ''}
                    </div>
                    {c.canary ? sampleTemplate(c.canary) : <span>-</span>}
                    {c.baseline &&
                        <div style={{marginTop: '10px'}}>
                            <div style={{fontSize: '12px', fontWeight: 600, marginBottom: '4px'}}>
                                {t('canary.baseline') + ' · ' + c.baseline.provider}
                            </div>
                            {sampleTemplate(c.baseline)}
                        </div>}
                </div>}
        </div>
    );

    const sampleTemplate = (m) => {
        const rows = FAIL_REASONS.concat(INFO_REASONS).filter(r => m.samples[r]).map(r => (
            <div key={r} style={{marginBottom: '0.4em'}}>
                <Tag value={r} severity={INFO_REASONS.includes(r) ? 'info' : 'danger'}
                     style={{marginRight: '0.5em'}}/>
                <a href={m.samples[r].url} target="_blank" rel="noreferrer">{m.samples[r].url}</a>
                <div style={{color: '#888', fontSize: '0.85em', marginTop: '0.2em'}}>{m.samples[r].exception}</div>
            </div>
        ));
        return rows.length ? <div>{rows}</div> : <span>{t('canary.no_failures')}</span>;
    };

    // ---- Breakdown lens: the same filtered docs, grouped by any dimension (absorbs the old
    // Request Analytics screen). Client-side group-by so a single fetch powers both lenses. ----
    const REASONS = ['ban', 'no_data', 'timeout', 'error'];
    const userName = (id) => { const u = (dimensions.users || []).find(x => x.id === id); return (u && u.name) || id; };
    const pct = (n, den) => den ? (100 * n / den) : 0;
    const rateTag = (p) => <Tag value={p.toFixed(1) + '%'} severity={p >= 98 ? 'success' : (p >= 95 ? 'warning' : 'danger')}/>;
    const opt = (arr) => (arr || []).map(v => ({label: v, value: v}));
    const num = (f) => (row) => (row[f] || 0).toLocaleString();
    const groupByOptions = [
        {label: t('request_analytics.by_host_provider'), value: 'host,provider'},
        {label: t('request_analytics.by_host'), value: 'host'},
        {label: t('request_analytics.by_provider'), value: 'provider'},
        {label: t('request_analytics.by_user'), value: 'user'},
        {label: t('request_analytics.by_day'), value: 'day'},
    ];
    const breakdownRows = (() => {
        const keys = groupBy.split(',');
        const map = {};
        filteredDocs.forEach(d => {
            const kv = keys.map(k => (k === 'day' ? d.date : d[k]));
            const id = kv.join('|');
            let m = map[id];
            if (!m) {
                m = map[id] = {id, attempts: 0, success: 0, samples: {}};
                keys.forEach((k, i) => { m[k] = kv[i]; });
                REASONS.forEach(r => { m[r] = 0; });
            }
            m.attempts += d.attempts || 0;
            m.success += d.success || 0;
            REASONS.forEach(r => { m[r] += (d.by_reason && d.by_reason[r]) || 0; });
            Object.entries(d.samples || {}).forEach(([r, s]) => { if (!m.samples[r]) m.samples[r] = s; });
        });
        return Object.values(map).sort((a, b) => b.attempts - a.attempts);
    })();
    const bkTotals = filteredDocs.reduce((acc, d) => {
        acc.attempts += d.attempts || 0; acc.success += d.success || 0;
        acc.ban += (d.by_reason && d.by_reason.ban) || 0; return acc;
    }, {attempts: 0, success: 0, ban: 0});
    const breakdownGroupBody = (g) => (row) => (g === 'user' ? userName(row.user) : (row[g] != null ? row[g] : '-'));
    const breakdownView = () => {
        if (!breakdownRows.length) return <div style={{color: 'var(--text-color-secondary)'}}>{t('canary.no_metrics')}</div>;
        const succ = pct(bkTotals.success, bkTotals.attempts);
        return <div>
            <div style={{display: 'flex', gap: '0.6em', alignItems: 'center', margin: '0.2em 0 1em', flexWrap: 'wrap'}}>
                <Tag value={t('request_analytics.total_requests') + ': ' + bkTotals.attempts.toLocaleString()}/>
                <Tag value={t('request_analytics.success_rate') + ': ' + succ.toFixed(1) + '%'}
                     severity={succ >= 98 ? 'success' : (succ >= 95 ? 'warning' : 'danger')}/>
                <Tag value={t('request_analytics.ban_rate') + ': ' + pct(bkTotals.ban, bkTotals.attempts).toFixed(1) + '%'}
                     severity={bkTotals.ban ? 'warning' : 'info'}/>
            </div>
            <DataTable value={breakdownRows} dataKey="id" paginator={breakdownRows.length > 50} rows={50}
                       expandedRows={expandedRows} onRowToggle={(e) => setExpandedRows(e.data)}
                       rowExpansionTemplate={(row) => <div style={{padding: '0.5em 1em'}}>{sampleTemplate({samples: row.samples})}</div>}>
                <Column expander={true} headerStyle={{width: '3em'}}/>
                {groupBy.split(',').map(g => (
                    <Column key={g} header={t('request_analytics.' + g)} body={breakdownGroupBody(g)}
                            sortable={true} sortField={g}/>))}
                <Column header={t('request_analytics.attempts')} body={num('attempts')} sortable={true} sortField="attempts"/>
                <Column header={t('request_analytics.success')} body={num('success')}/>
                <Column header={t('request_analytics.ban')} body={num('ban')}/>
                <Column header={t('request_analytics.no_data')} body={num('no_data')}/>
                <Column header={t('request_analytics.timeout')} body={num('timeout')}/>
                <Column header={t('request_analytics.error')} body={num('error')}/>
                <Column header={t('request_analytics.success_rate')} body={(row) => rateTag(pct(row.success, row.attempts))}/>
                <Column header={t('request_analytics.ban_rate')} body={(row) => <span>{pct(row.ban, row.attempts).toFixed(1)}%</span>}/>
            </DataTable>
        </div>;
    };

    // ---- Daily lens: the A/B comparison recomputed one day at a time, plus how the canary arm
    // moved against the previous day. Same filtered docs, same buildComparisons — only the
    // grouping differs, so the numbers always reconcile with the A/B lens. ----
    const dailyRows = (() => {
        const rows = [];
        Object.values(dailySeries(filteredAll)).forEach(series => {
            series.forEach((cur, i) => {
                const prev = i > 0 ? series[i - 1] : null;
                const rate = cur.canary ? cur.canary.successRate : null;
                // Only consecutive days are comparable; a day with no traffic breaks the chain
                // and leaves the delta empty rather than silently comparing across the gap.
                const prevRate = (prev && dayAfter(prev.date) === cur.date && prev.canary)
                    ? prev.canary.successRate : null;
                rows.push({
                    id: cur.host + '|' + cur.date, host: cur.host, date: cur.date,
                    canaryRate: rate, baselineRate: cur.baseline ? cur.baseline.successRate : null,
                    abDelta: cur.delta, attempts: cur.total,
                    dodDelta: (rate != null && prevRate != null)
                        ? Math.round((rate - prevRate) * 10) / 10 : null
                });
            });
        });
        return rows.filter(inWindow)
            .sort((a, b) => (a.host || '').localeCompare(b.host || '') || (a.date < b.date ? 1 : -1));
    })();
    const deltaTag = (value) => value == null
        ? <span style={{color: 'var(--text-color-secondary)'}}>-</span>
        : <Tag value={(value >= 0 ? '+' : '') + value}
               severity={value > 0 ? 'success' : (value < 0 ? 'danger' : 'info')}/>;
    const dailyRateBody = (field) => (row) => (
        <span style={{color: rateColor(row[field]), fontWeight: 600}}>
            {row[field] != null ? row[field] + '%' : '-'}
        </span>
    );
    const dailyView = () => {
        if (!dailyRows.length) return <div style={{color: 'var(--text-color-secondary)'}}>{t('canary.no_metrics')}</div>;
        return <DataTable value={dailyRows} dataKey="id" paginator={dailyRows.length > 50} rows={50}>
            <Column field="host" header={t('request_analytics.host')} sortable={true}/>
            <Column field="date" header={t('canary.day')} sortable={true}/>
            <Column field="canaryRate" header={t('canary.canary_arm')} body={dailyRateBody('canaryRate')} sortable={true}/>
            <Column field="baselineRate" header={t('canary.baseline')} body={dailyRateBody('baselineRate')} sortable={true}/>
            <Column field="abDelta" header={t('canary.ab_delta')} body={(row) => deltaTag(row.abDelta)} sortable={true}/>
            <Column field="dodDelta" header={t('canary.dod_delta')} body={(row) => deltaTag(row.dodDelta)} sortable={true}/>
            <Column field="attempts" header={t('canary.attempts')} body={num('attempts')} sortable={true}/>
        </DataTable>;
    };

    const selectedHostInfo = hosts.find(h => h.host === (rowData && rowData.host));

    const formBody = (
        <div className="p-grid alignCenter p-fluid">
            <div className="p-col-12">
                <label htmlFor="host">{t('canary.host')}</label>
                <AutoComplete id="host" value={rowData?.host} suggestions={hostSuggestions}
                              completeMethod={searchHosts} forceSelection={true} dropdown={true}
                              onChange={(e) => setRowData({...rowData, host: e.value})}
                              placeholder={t('canary.host')}/>
            </div>
            {selectedHostInfo && (selectedHostInfo.proxy_type || selectedHostInfo.proxy_country) ?
                <div className="p-col-12" style={{marginTop: '-0.6em', color: '#888', fontSize: '0.9em'}}>
                    {selectedHostInfo.proxy_type ? (t('canary.current_proxy') + ': ' + selectedHostInfo.proxy_type) : t('canary.no_proxy')}
                    {selectedHostInfo.proxy_country ? (' · ' + t('canary.country') + ': ' + selectedHostInfo.proxy_country) : ''}
                </div> : null}
            <div className="p-col-12">
                <label htmlFor="provider">{t('canary.provider')}</label>
                <Dropdown id="provider" name="provider" value={rowData?.provider} options={providers}
                          filter={true} onChange={onChange} placeholder={t('canary.provider')}/>
            </div>
            <div className="p-col-6">
                <label htmlFor="rate">{t('canary.rate')}</label>
                <InputNumber id="rate" suffix=" %" mode="decimal" minFractionDigits={0} maxFractionDigits={3} min={0} max={100}
                             value={rowData && rowData.rate != null ? Number((rowData.rate * 100).toFixed(3)) : null}
                             onValueChange={(e) => setRowData({...rowData, rate: e.value != null ? Number((e.value / 100).toFixed(6)) : null})}/>
            </div>
            <div className="p-col-6">
                <label htmlFor="enabled" style={{display: 'block'}}>{t('canary.enabled')}</label>
                <InputSwitch checked={rowData?.enabled === true} onChange={(e) => setRowData({...rowData, enabled: e.value})}/>
            </div>
        </div>
    );

    return <React.Fragment>

        <Dialog header={t('dialogs.canary_delete')} visible={showDeleteModal}
                style={{maxWidth: '50vw', minWidth: '20vw'}} breakpoints={{'960px': '75vw', '640px': '95vw'}}
                modal={true} onHide={() => { setShowDeleteModal(false); setRowData(newRule()); }}
                footer={<div>
                    <Button label={t('yes_no.yes')} icon="pi pi-check" className="p-button-danger" onClick={deleteRule}/>
                    <Button label={t('yes_no.no')} icon="pi pi-times" className="p-button-warning" onClick={() => { setShowDeleteModal(false); setRowData(newRule()); }}/>
                </div>}>
            {t('dialogs.canary_delete_msg', {host: rowData?.host})}
        </Dialog>

        <Dialog header={t('dialogs.canary_form')} visible={showFormModal}
                style={{maxWidth: '50vw', minWidth: '20vw'}} breakpoints={{'960px': '75vw', '640px': '95vw'}}
                modal={true} onHide={() => { setShowFormModal(false); setRowData(newRule()); }}
                footer={<div>
                    <Button label={t('actions.cancel')} icon="pi pi-times" className="p-button-warning" onClick={() => setShowFormModal(false)}/>
                    <Button label={t('actions.save')} icon="pi pi-check" className="p-button-success" onClick={saveRule}/>
                </div>}>
            {formBody}
        </Dialog>

        <Dialog header={t('canary.proxies') + (proxiesRule ? ' — ' + proxiesRule.host : '')}
                visible={!!proxiesRule} style={{maxWidth: '60vw', minWidth: '30vw'}}
                breakpoints={{'960px': '85vw', '640px': '95vw'}} modal={true}
                onHide={() => setProxiesRule(null)}>
            {proxiesRule ? eligibleTable(proxiesRule.host) : null}
        </Dialog>

        <SiteRuleDialog target={siteRuleTarget} setTarget={setSiteRuleTarget} onSave={saveSiteRule}
                        capabilityOptions={capabilityOptions}
                        siteOptions={siteRuleTarget ? [{label: siteRuleTarget.host, value: siteRuleTarget.host}] : []}/>

        <div className="card management-page">
            <AdminPageHeader
                title={t('menu_item.canary')}
                subtitle={t('menu.management_hub.page.canary')}
                actions={<React.Fragment>
                    <span className="p-input-icon-left">
                        <i className="pi pi-search"/>
                        <InputText value={search} onChange={(e) => setSearch(e.target.value)}
                                   placeholder={t('canary.search_site')} style={{width: '160px'}}/>
                    </span>
                    <Dropdown value={days} options={DAY_OPTIONS} onChange={(e) => setDays(e.value)}
                              style={{width: '92px'}}/>
                    <Button icon="pi pi-refresh" className="p-button-text" tooltip={t('actions.refresh')}
                            onClick={() => { loadRules(); loadMetrics(); }}/>
                    <Button label={t('actions.add')} icon="pi pi-plus" className="p-button-success"
                            onClick={() => { setRowData(newRule()); setShowFormModal(true); }}/>
                </React.Fragment>}
            />

            <div style={{display: 'flex', alignItems: 'center', gap: '0.7em', marginBottom: '0.9em',
                         flexWrap: 'wrap'}}>
                <h5 style={{margin: 0}}>{t('canary.rules_title')}</h5>
                <span style={{color: 'var(--text-color-secondary)', fontSize: '0.88em'}}>
                    {t('canary.overview_line', {
                        sites: rules.length,
                        attempts: overall.attempts.toLocaleString(),
                        rate: overall.attempts
                            ? Math.round(overall.success / overall.attempts * 1000) / 10 : 0})}
                </span>
                {fltHost &&
                    <Button label={t('canary.showing_host', {host: fltHost})} icon="pi pi-times"
                            className="p-button-text p-button-sm" onClick={() => setFltHost(null)}/>}
            </div>
            {loading || metricsLoading ?
                <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div> :
                (!rules.length
                    ? <div style={{color: 'var(--text-color-secondary)'}}>{t('actions.empty_message')}</div>
                    : (!sites.length
                        ? <div style={{color: 'var(--text-color-secondary)'}}>{t('canary.no_match')}</div>
                        : <CanarySiteBands
                            t={t} sites={sites} fltHost={fltHost} setFltHost={setFltHost}
                            armCard={armCard} onToggle={toggleEnabled} onSiteRule={openSiteRule}
                            onEdit={(rule) => {
                                setRowData({host: rule.host, provider: rule.canary.provider,
                                            rate: rule.canary.rate, enabled: rule.canary.enabled !== false});
                                setShowFormModal(true);
                            }}
                            onDelete={(rule) => {
                                setRowData({host: rule.host, ...rule.canary});
                                setShowDeleteModal(true);
                            }}
                            onProxies={(rule) => {
                                setProxyFilters({source: null, type: null, enabled: null});
                                setProxiesRule(rule);
                            }}/>))}

            <div style={{marginTop: '2.5em', background: '#f7f9fc',
                         border: '1px solid var(--surface-border)', borderRadius: '14px', padding: '1.3em 1.5em'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                         flexWrap: 'wrap', gap: '0.6em', marginBottom: '0.2em'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.9em', flexWrap: 'wrap'}}>
                    <h5 style={{margin: 0}}>{t('canary.results_title')}</h5>
                    <SelectButton value={resultView} onChange={(e) => e.value && setResultView(e.value)}
                                  options={[{label: t('canary.view_ab'), value: 'ab'},
                                            {label: t('canary.view_breakdown'), value: 'breakdown'},
                                            {label: t('canary.view_daily'), value: 'daily'}]}/>
                </div>
                {fltHost &&
                    <Button label={t('canary.showing_host', {host: fltHost})} icon="pi pi-times"
                            className="p-button-text p-button-sm" onClick={() => setFltHost(null)}/>}
            </div>

            <div className="p-grid p-fluid" style={{alignItems: 'flex-end', margin: '0.4em 0 0.6em'}}>
                <div className="p-col-6 p-md-4"><label>{t('request_analytics.provider')}</label>
                    <Dropdown value={fltProvider} options={opt(dimensions.providers)} showClear={true} filter={true}
                              placeholder={t('request_analytics.all')} onChange={(e) => setFltProvider(e.value)}/></div>
                <div className="p-col-6 p-md-4"><label>{t('request_analytics.user')}</label>
                    <Dropdown value={fltUser} options={(dimensions.users || []).map(u => ({label: u.name, value: u.id}))}
                              showClear={true} filter={true} placeholder={t('request_analytics.all')}
                              onChange={(e) => setFltUser(e.value)}/></div>
                {resultView === 'breakdown' &&
                    <div className="p-col-6 p-md-4"><label>{t('request_analytics.group_by')}</label>
                        <Dropdown value={groupBy} options={groupByOptions} onChange={(e) => setGroupBy(e.value)}/></div>}
            </div>

            {metricsLoading ?
                <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div> :
                (resultView === 'ab'
                    ? (comparisons.length ?
                        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                            {comparisons.map(comparisonCard)}
                        </div> :
                        <div style={{color: 'var(--text-color-secondary)'}}>{t('canary.no_metrics')}</div>)
                    : (resultView === 'daily' ? dailyView() : breakdownView()))}

            <div style={{marginTop: '1.4em', fontSize: '0.85em', color: 'var(--text-color-secondary)'}}>
                <i className="pi pi-info-circle" style={{marginRight: '5px'}}/>
                {t('canary.infra_hint')} <a href="#/proxy_monitor">{t('canary.infra_link')}</a>
            </div>
            </div>
        </div>

    </React.Fragment>;
};
export default Canary;
